import { Box, Divider, Typography, useTheme } from '@mui/material';
import { Fragment } from 'react';
import { Link as RouterLink } from 'react-router';
import { SDLC_PATH } from '../../config';
import { countsLine } from '../../content';
import { cardSurfaceSx, contentTypeColourFor, stageColourFor, stageLabelFor, tokens } from '../../theme';
import type { SearchResultCardProps } from './types';

/**
 * One result — phase, process or method, drawn the same way whichever it is.
 *
 * **Two colour axes, told apart by treatment rather than by hue.** The stage is
 * the 4px top edge and the uppercase label; the type is the filled pill. That
 * matters because three of the six values coincide — a Build card's edge and a
 * Method badge are literally the same blue.
 *
 * **The card is not itself a link**, because its trail holds four; same
 * resolution the process cards took.
 */
export const SearchResultCard = ({ result, reference, trail }: SearchResultCardProps) => {
  const theme = useTheme();
  // The edge carries nothing and the label is literal text, so both are ink on
  // the card rather than a ground under white.
  const stageColour = stageColourFor(result.stage, 'onSurface');
  const typeColour = contentTypeColourFor(theme.palette, result.type);
  const counts = countsLine(result);

  return (
    <Box
      component="li"
      sx={{
        ...cardSurfaceSx,
        // A child rather than a border-top, so it keeps the card's radius
        // instead of squaring off the top corners.
        overflow: 'hidden',
        listStyle: 'none',
      }}
    >
      <Box sx={{ height: 4, backgroundColor: stageColour }} />

      <Box sx={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 4,
          }}
        >
          <Typography variant="caption" component="p" sx={{ color: 'text.meta' }}>
            {/* The reference, then the stage in its own colour. Both glyphs are
                the component's — the API returns clean strings. */}
            {reference}
            <Box component="span" aria-hidden sx={{ paddingInline: 2, color: 'text.disabled' }}>
              &bull;
            </Box>
            <Box component="span" sx={{ color: stageColour, fontWeight: 500 }}>
              {stageLabelFor(result.stage)}
            </Box>
          </Typography>

          <Typography
            variant="caption"
            component="span"
            sx={{
              flexShrink: 0,
              paddingInline: 3,
              paddingBlock: 1,
              borderRadius: `${String(tokens.radius.full)}px`,
              backgroundColor: typeColour.surface,
              color: typeColour.colour,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {result.type}
          </Typography>
        </Box>

        <Typography variant="h3" component="h3">
          <RouterLink
            to={`${SDLC_PATH}/${result.path}`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {result.name}
          </RouterLink>
        </Typography>

        <Trail trail={trail} />

        {/*
          **The designs leave this slot empty and it is filled here.** The
          Description node in the Figma frame is collapsed to nothing, which
          reads as a slot the designer emptied rather than one they removed —
          and a search result with no line of prose under it is a link rather
          than a result. Two lines: enough to tell two similarly-named methods
          apart, not enough to stop the page being scannable.
        */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {result.description}
        </Typography>

        {/*
          A method carries no counts, so it gets no footer and no rule above one
          — an empty strip under a divider reads as content that failed to
          arrive. The designs put an AI tool here beside the counts; a summary
          carries no tools, so there is nothing to put.
        */}
        {counts !== undefined && (
          <>
            <Divider />
            <Typography variant="caption" component="p" sx={{ color: 'text.meta' }}>
              {counts}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

/**
 * `SDLC / Experience Design / User Research / Field Study`.
 *
 * **Not the `Breadcrumbs` component**, which is a `nav` landmark naming the page
 * you are on — ten results would put ten such landmarks in the document, none of
 * them describing where the reader is.
 *
 * **The last step is plain text and not blue**, because it names the record the
 * title above already links to.
 */
const Trail = ({ trail }: { trail: SearchResultCardProps['trail'] }) => (
  <Typography variant="caption" component="p" sx={{ color: 'text.link' }}>
    {trail.map((step, index) => (
      <Fragment key={step.label}>
        {index > 0 && (
          <Box component="span" aria-hidden sx={{ paddingInline: 2, color: 'text.disabled' }}>
            /
          </Box>
        )}
        {step.to === undefined ? (
          <Box component="span" sx={{ color: 'text.meta' }}>
            {step.label}
          </Box>
        ) : (
          <Box component="span" sx={{ '&:hover': { textDecoration: 'underline' } }}>
            <RouterLink to={step.to} style={{ color: 'inherit', textDecoration: 'none' }}>
              {step.label}
            </RouterLink>
          </Box>
        )}
      </Fragment>
    ))}
  </Typography>
);
